import { makeStyles, Text, tokens } from '@fluentui/react-components';

import { spacing } from '@/theme/tokens';
import type { AiUsageReport } from '@/api/types';

interface SummaryCardsProps {
  report: AiUsageReport;
}

const useStyles = makeStyles({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: spacing.l,
    marginBottom: spacing.xxxl,
  },
  card: {
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusXLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: spacing.l,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  },
  value: {
    fontSize: '28px',
    lineHeight: '32px',
    fontWeight: 600,
    fontFeatureSettings: '"tnum" 1',
  },
  label: {
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    fontSize: '11px',
  },
});

const usd = (v: number): string =>
  v.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

const integer = (v: number): string => v.toLocaleString();

export function SummaryCards({ report }: SummaryCardsProps) {
  const classes = useStyles();
  const totals = report.monthsData.reduce(
    (acc, m) => ({
      cost: acc.cost + m.totalCostUsd,
      actions: acc.actions + m.userActions,
      pages: acc.pages + m.pages,
    }),
    { cost: 0, actions: 0, pages: 0 },
  );
  const avgPerAction = totals.actions > 0 ? totals.cost / totals.actions : 0;

  const cells: { label: string; value: string }[] = [
    { label: 'Total cost', value: usd(totals.cost) },
    { label: 'User actions', value: integer(totals.actions) },
    { label: 'Avg $/action', value: usd(avgPerAction) },
    { label: 'Pages processed', value: integer(totals.pages) },
  ];

  return (
    <div className={classes.grid}>
      {cells.map((c) => (
        <div key={c.label} className={classes.card}>
          <Text className={classes.label}>{c.label}</Text>
          <Text className={classes.value}>{c.value}</Text>
        </div>
      ))}
    </div>
  );
}
